import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';

import { combineLatest, Subject, takeUntil } from 'rxjs';
import { NavigationService } from '../../../../core/navigation/navigation.service';
import { FuseFullscreenComponent } from '../../../../shared/@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '../../../../shared/@fuse/components/loading-bar';
import { FuseVerticalNavigationComponent, FuseNavigationService, FuseNavigationItem } from '../../../../shared/@fuse/components/navigation';
import { FuseMediaWatcherService } from '../../../../shared/@fuse/services/media-watcher';
import { LanguagesComponent } from '../../../common/languages/languages.component';
import { MessagesComponent } from '../../../common/messages/messages.component';
import { NotificationsComponent } from '../../../common/notifications/notifications.component';
import { QuickChatComponent } from '../../../common/quick-chat/quick-chat.component';
import { SearchComponent } from '../../../common/search/search.component';
import { ShortcutsComponent } from '../../../common/shortcuts/shortcuts.component';
import { UserComponent } from '../../../common/user/user.component';
import { Navigation } from '../../../../core/navigation/navigation.types';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SettingsComponent } from "../../../common/settings/settings.component";
import { AuthService } from 'app/core/auth/auth.service';
import { ChipModule } from 'primeng/chip';
import { TagModule } from 'primeng/tag';
import { UtilsAmcoService } from 'app/core/services';
import { EPermissionType } from 'app/core/statics';
import { UserData, UserDataAmco } from 'app/core/models';
import { UserService } from 'app/core/user/user.service';

@Component({
    selector: 'dense-layout',
    templateUrl: './dense.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        CommonModule,
        MatTooltipModule,
        MatIconModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        SearchComponent,
        ShortcutsComponent,
        MessagesComponent,
        NotificationsComponent,
        UserComponent,
        RouterOutlet,
        QuickChatComponent,
        SettingsComponent,

        ChipModule,
        TagModule
    ],
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    onHover: boolean;
    navigationAppearance: 'default' | 'dense' = 'dense';
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    public userData: UserData | UserDataAmco;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _authService: AuthService,
        private utilsService: UtilsAmcoService,
        private userService: UserService
    ) {
        combineLatest([
            this._navigationService.navigation$,
            this.utilsService.getPerms()
        ]).subscribe(([navigation, perms]) => {

            // Filtra gli elementi in base ai permessi
            let defaultItems = navigation.default.filter((item: FuseNavigationItem) => {
                return perms[item.id] !== undefined && perms[item.id] !== EPermissionType.HIDDEN;
            });

            // Recupera l'elemento logout e assegna la funzione di logout
            const logoutItem: FuseNavigationItem = {
                ...navigation.default.find((item: FuseNavigationItem) => item.id === 'logout'),
                function: () => this.signOut()
            };

            // Aggiorna la navigazione mantenendo logout sempre presente
            this.navigation = {
                ...navigation,
                default: [...defaultItems, logoutItem]
            };
        });

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        if (this.userService.userData) {
            this.userData = this.userService.userData;
        }

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');

                // Change the navigation appearance
                this.navigationAppearance = this.isScreenSmall
                    ? 'default'
                    : 'dense';
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------


    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Toggle the navigation appearance
     */
    toggleNavigationAppearance(): void {
        this.navigationAppearance =
            this.navigationAppearance === 'default' ? 'dense' : 'default';
    }

    private signOut(): void {
        const userData = this.userService.userData;

        if (userData?.jwt) { // Verifica che esista un token JWT
            const token = userData?.tokenUrl;

            if (token) {
                this._authService.logout().subscribe({
                    next: () => {
                        this.userService.clearUserData(); // Rimuove i dati solo dopo il successo
                        this._router.navigate(['/sign-in'], { queryParams: { token } });
                    },
                    error: (err) => {
                        console.error("Errore durante il logout:", err);
                    }
                });
                return; // Evita di eseguire il logout due volte
            }
        }

        this._authService.logout().subscribe({
            next: () => {
                this.userService.clearUserData();
                this._router.navigate(['/sign-in']);
            },
            error: (err) => {
                console.error("Errore durante il logout:", err);
            }
        });
    }

}
